import {Injectable} from '@angular/core';
import {AssignmentModel} from '../../models/assignment.model';
import {ApiResponseMessage, ConnectorService} from './connector.service';
import {forkJoin, map, Observable, Subject, switchMap, tap} from 'rxjs';
import {
    IAssignmentUpdateResult,
    ITaskModelResponse,
    TaskCollectionModel,
    TaskCommentModel,
    TaskLifeModel,
    TaskModel,
    UserTasksSettings
} from '../../models/task.model';
import {
    BoardGroupModel,
    BoardTableModel,
    MemberPermissionsModel,
    StatusNodeModel,
    TaskBoardModel,
    TaskTypesModel
} from '../../models/tasktable.model';
import {MemberModel} from '../../models/user.model';
import {PaginationModel} from '../../models/file-table.model';

@Injectable({
    providedIn: 'root',
})
export class CrmService extends ConnectorService {
    /** AAA
     * Все доступные задачи пользователя одинм балком через максимальную пагинацию
     * @param pagination - пагинация и сортировка
     * @param userGuid
     * @returns
     */
    public subTaskData: Subject<ITaskModelResponse> = new Subject()

    /**
     * Создание задачи
     * @param data {
     * title: string
     * content ?: string
     * deadline_at ?: string 10/10/2021 21:21
     * importance ?: Normal | Middle | Important
     * }
     */
    createTask(data: {
        title: any;
        content: string;
        board_alias: any;
        task_type_id: number;
    }): Observable<{ task: TaskModel }> {
        return this.post('instance/crm/task', data);
    }

    /**
     * Список задач из црм
     *
     * @param userGuid - если не указан берутся задачи для текущего пользователя
     * @param filter = {date_from: string, date_to: string} // неактуально
     * @param pagination
     */
    getTasks(userGuid: string | null = null, filter = null, pagination: PaginationModel,): Observable<ITaskModelResponse> {
        return this.get('instance/crm/task/related?guid=' + userGuid, pagination);
        // if (userGuid) {
        /*         } else if (filter) {
                    return this.get('instance/crm/task/schedule/', {pagination, filter});
                } */
    }

    /** AAA
     * Задачи пользователя по нескольким бизнес процессам одним балком
     * @param pagination - пагинация и сортировка
     * @param userGuid
     * @param boards - массив алиасов бизнес процессов
     * @returns
     */
    getAllTasksByPagesManyBoards(userGuid: string | null = null, filter = null, pagination: PaginationModel, boards: TaskBoardModel[]): Observable<TaskModel[]> {
        let reqBulk: Observable<TaskModel[]>[] = []
        boards.forEach(board => reqBulk.push(this.getAllTasksByPages(userGuid, filter, {
            ...pagination,
            board_alias: board.alias
        })))
        return forkJoin(reqBulk).pipe(map(res => res.flat()))
    }

    public getAllTasksByPages(userGuid: string | null = null, filter = null, pagination: PaginationModel,): Observable<TaskModel[]> {
        let reqBulk: Observable<TaskModel[]>[] = [];
        return this.getTasks(userGuid, filter, pagination).pipe(
            tap(res => this.subTaskData.next(res)),
            tap(res => {
                for (let index = res.current_page; index <= (res.page_count === 0 ? 1 : res.page_count); index++) {
                    reqBulk.push(this.getTasks(userGuid, filter, {
                        ...pagination,
                        current_page: index
                    }).pipe(map(res => res.data)))
                }
            }),
            switchMap(() => forkJoin(reqBulk)),
            map(res => res.flat())
        )
    }

    /**
     * Получить детали по задаче
     * @param taskAlias
     */
    getTask(taskAlias: string,domain?:string): Observable<{
        importance_indexes: number[],
        task: TaskModel,
        rights: MemberPermissionsModel
    }> {
        return this.get('instance/crm/task/' + taskAlias,undefined,domain);
    }

    /**
     * обновление задачи
     *  data:
     *      {property: new_val}
     * @param taskAlias
     * @param data
     */
    editTask(taskAlias: string, data: object): Observable<{ task: TaskModel, message: string }> {
        return this.put('instance/crm/task/' + taskAlias, data);
    }

    /**
     * Установить, удалить или обновить значение проперти
     * @param taskAlias
     * @param propertyId
     * @param value
     */
    setOrUpdateProperty(taskAlias: string | number, propertyId: number, value: { value: any }): Observable<any> {
        return this.put('instance/crm/task/properties/' + taskAlias + '/' + propertyId, value);
    }

    /**
     * Обновление статуса задачи
     * @param taskAlias
     * @param statusNodeId
     * @param pin
     */
    updateTaskStatus(taskAlias: string, statusNodeId: number, pin: string): Observable<{
        task: TaskModel,
        message: string
    }> {
        return this.post('instance/crm/task/' + taskAlias + '/status', {status_node_id: statusNodeId, PIN_code: pin});
    }

    /**
     * Добавление коллекции к задаче
     * @param taskAlias
     * @param collectionId
     */
    addCollectionInTask(taskAlias: string, collectionId: number): Observable<ApiResponseMessage> {
        return this.post('instance/crm/task/' + taskAlias + '/collections/', {collection_id: collectionId});
    }

    /**
     * Добавление пользователя в задачу
     * @param taskAlias
     * @param userData {user_id, role}
     */
    addUserInTask(taskAlias: string, userData: { guid: string; role: string; }): Observable<ApiResponseMessage> {
        return this.post('instance/crm/task/' + taskAlias + '/users/', userData);
    }

    /**
     * Удаление пользователя с задачи
     * @param taskAlias
     * @param data
     */
    deleteUserFromTask(taskAlias: string, data: { guid: string, role: string }): Observable<ApiResponseMessage> {
        return this.delete('instance/crm/task/' + taskAlias + '/users/', data);
    }

    /**
     * Удаление задачи
     * @param taskAlias
     */
    deleteTask(taskAlias: string): Observable<ApiResponseMessage> {
        return this.delete('instance/crm/task/' + taskAlias);
    }

    /**
     * Отправка комментария к задаче
     * @param taskAlias
     * @param value
     */
    sendTaskComment(taskAlias: string, value: string): Observable<ApiResponseMessage> {
        return this.post('instance/crm/task/' + taskAlias + '/comments/', {comment: value});
    }

    /**
     * Выгрузка комментариев к задаче
     * @param taskAlias
     */
    getTaskComments(taskAlias: string): Observable<{ comments: TaskCommentModel[] }> {
        return this.get('instance/crm/task/' + taskAlias + '/comments/');
    }

    /**
     * Список коллекций задачи
     * @param taskAlias
     */
    getTaskCollections(taskAlias: string): Observable<{ data: TaskCollectionModel[] }> {
        return this.get('instance/crm/task/' + taskAlias + '/collections/');
    }

    /**
     * Удаление коллекции из задачи
     * @param taskAlias
     * @param collectionId
     */
    deleteCollectionFromTask(taskAlias: string, collectionId: number): Observable<ApiResponseMessage> {
        return this.delete('instance/crm/task/' + taskAlias + '/collections/' + collectionId);
    }

    /**
     * Добавление изображения в задачу
     * @param taskAlias
     * @param photo
     */
    addImageToTask(taskAlias: string, photo: { photo: Blob | MediaSource }): Observable<{ message: string }> {
        const form = new FormData();
        for (const dataKey of Object.keys(photo)) {
            form.append(dataKey, photo[dataKey]);
        }
        return this.post('instance/crm/task/' + taskAlias + '/photo', form, false);
    }

    /**
     * Удаление изображения из задачи
     * @param taskAlias
     * @param photoId
     */
    deleteImageInTask(taskAlias: string, photoId: string): Observable<{ message: string }> {
        return this.delete('instance/crm/task/' + taskAlias + '/photo/' + photoId);
    }

    /**
     * Получение списка досок
     */
    getAllBoards(data?: { member_guid: string }): Observable<{ boards: BoardTableModel[], groups: BoardGroupModel[] }> {
        return this.get('instance/crm/boards', data);
    }

    /**
     * Создание доски
     * @param data
     */
    createDashboard(data: {
        name: string,
        alias: string,
        icon: string,
        description: string,
        group_id: number,
        need_to_create_statuses: boolean,
    }): Observable<{ task_board: BoardTableModel }> {
        return this.post('instance/crm/boards', data);
    }

    /**
     * Создание группы досок
     * @param title
     */
    createDashboardGroup(title: string): Observable<{ group: BoardGroupModel }> {
        return this.post('instance/crm/groups/group', {title});
    }

    /**
     * Изменение названия группы досок
     * @param groupId
     * @param title - новое название
     */
    updateGroupTitle(groupId: number, title: string): Observable<{ group: BoardGroupModel }> {
        return this.put('instance/crm/groups/' + groupId, {title});
    }

    /**
     * Удаление группы бизнес-процессов
     * @param groupId
     */
    deleteGroupById(groupId: number): Observable<{ message: string }> {
        return this.delete('instance/crm/groups/delete_group/' + groupId);
    }

    /**
     * Получение задач из доски
     * @param boardAlias
     * @param boardAlias
     * @param pagination
     */
    getTasksByBoard(boardAlias: string, pagination: PaginationModel): Observable<{
        data: TaskModel[],
        importance_indexes: number[],
        colons: UserTasksSettings[],
    }> {
        return this.get('instance/crm/boards/' + boardAlias + '/tasks', pagination);
    }

    /**
     * Удаление доски по ID
     * @param boardAlias
     * @param isArchive
     */
    deleteBoardById(boardAlias: string, isArchive: boolean): Observable<{ message: string }> {
        return this.put('instance/crm/boards/' + boardAlias + '/archive', {is_archived: isArchive});
    }

    /**
     * Обновление существующей доски (название, ключ, иконка, описание)
     * @param boardAlias
     * @param data
     */
    updateBoard(boardAlias: string, data: {
        name: string,
        alias: string,
        icon: string,
        description: string,
        group_id: number,
    }): Observable<{ board: BoardTableModel }> {
        return this.put('instance/crm/boards/' + boardAlias, data);
    }

    /**
     * Добавление узла(статуса)
     * @param boardAlias
     * @param data
     */
    addNode(boardAlias: string,
            data: {
                label: string,
                description: string,
                target_ids: string,
                source_ids: string,
                is_successful: boolean,
                is_universal: boolean,
                data: string,
            }): Observable<{ board: BoardTableModel }> {
        return this.post('instance/crm/boards/node-status/' + boardAlias, data);
    }

    /**
     * Добавление взаимосвязи между узлами(статусами)
     * @param boardAlias
     * @param data
     */
    addEdge(boardAlias: string, data: { source_id: string, target_id: string }): Observable<{
        board: BoardTableModel
    }> {
        return this.post('instance/crm/boards/edge/' + boardAlias, data);
    }

    /**
     * Редактирование узла(статуса)
     * @param statusNodeId
     * @param data
     */
    updateNode(statusNodeId: string,
               data: {
                   label: string,
                   description: string,
                   is_successful: boolean,
                   to_archive: boolean,
                   is_universal: boolean,
                   data: string
               }): Observable<{ board: BoardTableModel }> {
        return this.put('instance/crm/boards/node-status/' + statusNodeId, data);
    }

    /**
     * Удаление узла(статуса)
     * @param statusNodeId
     * @param newStatusNodeId
     */
    deleteNode(statusNodeId: string, newStatusNodeId: string): Observable<{ board: BoardTableModel }> {
        return this.delete('instance/crm/boards/node-status/' + statusNodeId + '/' + newStatusNodeId);
    }

    /**
     * Удаление взаимосвязи между узлами(статусами)
     * @param edgeId
     */
    deleteEdge(edgeId: number): Observable<{ board: BoardTableModel }> {
        return this.delete('instance/crm/boards/edge/' + edgeId);
    }

    /**
     * Получение доски по alias (для отображения графа и рендера колонок со статусами)
     * @param boardAlias
     * @returns
     */
    getBoardByAlias(boardAlias: string): Observable<{ board: TaskBoardModel, rights: any }> {
        return this.get('instance/crm/boards/' + boardAlias);
    }

    /**
     * Создание типа задачи
     * @param data
     */
    createTaskType(data: { board_alias: string, name: string, icon: string }): Observable<{
        task_type: TaskTypesModel
    }> {
        return this.post('instance/crm/boards/task-types', data);
    }

    /**
     * Изменение типа задачи
     * @param taskTypeId
     * @param data
     */
    updateTaskType(taskTypeId: number, data: { name: string, icon: string }): Observable<{ task: TaskModel }> {
        return this.put('instance/crm/boards/task-types/' + taskTypeId, data);
    }

    /**
     * Создать копию доски
     * @param boardAlias
     * @param data
     */
    copyTaskBoard(boardAlias: string, data: { name: string, alias: string, icon: string, description: string }):
        Observable<{ board: BoardTableModel }> {
        return this.post('instance/crm/boards/' + boardAlias + '/clone', data);
    }

    /**
     * Удаление типа задачи
     * @param taskTypeId
     * @param newTaskTypeId
     */
    deleteTaskType(taskTypeId: number, newTaskTypeId: number): Observable<{ message: string }> {
        return this.delete('instance/crm/boards/task-types/' + taskTypeId + '?new_task_type_id=' + newTaskTypeId);
    }

    /**
     * Установка пользовательских настроек статусов для доски / колонок полей таблицы для доски или моих задач
     * @param settingsData
     *         type_setting - 'board_statuses' настройка видимости статусов доски, необходимо указать board_alias;
     *                        'board' настройка видимости полей таблицы для доски, необходимо указать board_alias;
     *                        'tasks_all_boards' настройка видимости полей таблицы для раздела "мои задачи";
     *         value - ID статусов (если настраиваются статусы доски) или KEY полей таблицы в формате Array.toString()
     */
    setUserStatusOrPropertiesSettings(
        settingsData: {
            type_setting: 'board' | 'board_statuses' | 'tasks_all_boards',
            board_alias?: string,
            value: string,
        }
    ): Observable<any> {
        return this.put('instance/crm/custom/settings', settingsData);
    }

    /**
     * Получение пользовательских настроек статусов для доски / колонок полей таблицы для доски или моих задач
     * @param typeSetting - 'board_statuses' получение настройки видимости статусов доски, необходимо указать board_alias;
     *                      'board' получение настройки видимости полей таблицы для доски, необходимо указать board_alias;
     *                      'tasks_all_boards' получение настройки видимости полей таблицы для раздела "мои задачи";
     * @param boardAlias
     */
    getUserStatusOrPropertiesSettings(
        typeSetting: 'board' | 'board_statuses' | 'tasks_all_boards',
        boardAlias?: string
    ): Observable<{ setting: UserTasksSettings[] }> {
        return this.get('instance/crm/custom/settings/' + typeSetting + '?board_alias=' + boardAlias);
    }

    /**
     * Загрузка логов задачи
     * @param taskAlias
     * @param pagination
     */
    getTaskLifeByAlias(taskAlias: string, pagination: PaginationModel): Observable<{
        current_page: number,
        page_count: number,
        data: TaskLifeModel[]
    }> {
        return this.get('instance/crm/task/' + taskAlias + '/life?current_page=' + pagination.current_page +
            '&limit=' + pagination.limit
        );
    }

    /**
     * Получение прав доступа пользователей и департаментов в доску
     * @param alias
     * @returns
     */
    getBoardRights(alias: string): Observable<any> {
        return this.get('instance/crm/boards/' + alias + '/rights');
    }

    /**
     * Добавить права на бизнес-процесс
     * @param boardAlias
     * @param data {
     *             role_type: 'member' | 'department' | 'division',
     *             role_key: string | number,
     *             is_read_all_tasks: boolean, - право на чтение всех задач
     *             is_create_new_task: boolean, - право на создание задач в бизнес-процессе
     *             is_create_new_comment: boolean, - право на создание комментариев в задачах
     *             is_set_property_val: boolean, - право на установку значений кастомних полей задач
     *             is_edit_settings: boolean, - право на редактирование доски
     *         }
     */
    setBoardsRights(
        boardAlias: string,
        data: {
            role_type: 'member' | 'department' | 'division',
            role_key: string | number,
            is_create_new_comment: string | 'succession' | 'allowed' | 'denied';
            is_create_new_task: string | 'succession' | 'allowed' | 'denied';
            is_edit_settings: string | 'succession' | 'allowed' | 'denied';
            is_read_all_tasks: string | 'succession' | 'allowed' | 'denied';
            is_set_property_val: string | 'succession' | 'allowed' | 'denied';
            is_set_self_in_task: string | 'succession' | 'allowed' | 'denied';
            is_set_other_in_task: string | 'succession' | 'allowed' | 'denied';
        }
    ): Observable<any> {
        return this.put('instance/crm/boards/' + boardAlias + '/rights', data);
    }

    /**
     * Удалить право бизнес-процесса
     * @param boardAlias
     * @param data {
     *                 role_type: 'member' | 'department' | 'division',
     *                 role_key: string | number
     *             }
     */
    deleteBoardsRights(boardAlias: string,
                       data: {
                           role_type: 'member' | 'department' | 'division',
                           role_key: string | number
                       }): Observable<any> {
        return this.delete('instance/crm/boards/' + boardAlias + '/rights', data);
    }

    /**
     * Удаление комментария
     * @param commentId
     * @returns
     */
    deleteTaskComment(commentId: number): Observable<{ message: string }> {
        return this.delete('instance/crm/task/' + commentId + '/comments');
    }

    /**
     * Редактирование комментария
     * @param commentId
     * @param commentContent
     */
    editTaskComment(commentId: number, commentContent: string): Observable<{ message: string }> {
        return this.put('instance/crm/task/' + commentId + '/comments', {comment: commentContent});
    }

    /**
     * Рекомендованные пользователи
     * @param boardAlias
     */
    recommendedUsersForTask(boardAlias: string): Observable<{
        executor: {
            number_of_mentions_in_other_tasks: number,
            member: MemberModel
        }[],
        observer: {
            number_of_mentions_in_other_tasks: number,
            member: MemberModel
        }[],
        responsible: {
            number_of_mentions_in_other_tasks: number,
            member: MemberModel
        }[]
    }> {
        return this.get('instance/crm/boards/' + boardAlias + '/recommended-roles');
    }

    /**
     * Дочерние задачи
     * @param taskAlias
     * @returns
     */
    getSubtasks(taskAlias: string): Observable<{ subtasks: TaskModel[], colons: UserTasksSettings[] }> {
        return this.get('instance/crm/task/' + taskAlias + '/subtasks');
    }

    /**
     * Получение родительских подзадач
     * @param taskAlias
     */
    getParentsTasks(taskAlias: string): Observable<{ parent_tasks: TaskModel[], colons: UserTasksSettings[] }> {
        return this.get('instance/crm/task/' + taskAlias + '/parent-tasks');
    }

    /**
     * Добавить дочернюю задачу
     * @param taskAlias
     * @param subtaskAlias
     * @returns
     */
    addSubtask(taskAlias: string, subtaskAlias: string): Observable<{ message: string }> {
        return this.post('instance/crm/task/' + taskAlias + '/subtask/' + subtaskAlias);
    }

    /**
     * Удалить дочернюю задачу
     * @param taskAlias
     * @param subtaskAlias
     * @returns
     */
    deleteSubtask(taskAlias: string, subtaskAlias: string): Observable<{ message: string }> {
        return this.delete('instance/crm/task/' + taskAlias + '/subtask/' + subtaskAlias);
    }

    /**
     * Получение связей досок
     */
    getBoardsForGraph(): Observable<any> {
        return this.get('instance/crm/boards/board-links');
    }

    /**
     * Получение изображений
     * @param taskAlias
     * @param imageUkey
     * @returns
     */
    getTaskImage(taskAlias: string, imageUkey: string): Observable<any> {
        return this.getBodyFile('instance/crm/task/' + taskAlias + '/image/' + imageUkey);
    }

    /**
     * Создание связи между досками для передачи задач
     * @param data
     */
    createCopyTaskLinkBetweenBoards(data: { status_node_id: number, board_alias: string }): Observable<{
        message: string,
        status_from: StatusNodeModel,
        status_to: StatusNodeModel
    }> {
        return this.post('instance/crm/boards/node-status/board-links', data);
    }

    /**
     * Удаление связи между досками
     * @param outputNodeId
     * @param targetBoardAlias
     */
    deleteLinkBetweenBoards(outputNodeId: string, targetBoardAlias: string): Observable<any> {
        return this.delete('instance/crm/boards/node-status/' + outputNodeId + '/board-links/' + targetBoardAlias);
    }

    /**
     * Получение последних 100 изменений в доске
     * @param boardAlias
     * @param pagination
     */
    getBoardLife(boardAlias: string, pagination: PaginationModel): Observable<{
        current_page: number,
        data: TaskLifeModel[],
        page_count: number
    }> {
        return this.get('instance/crm/statistics/' + boardAlias + '/board-life?' +
            'current_page=' + pagination.current_page +
            '&limit=' + pagination.limit);
    }

    /**
     * Добавление пользователя в статус
     * @param boardAlias
     * @param data
     */
    addMemberToStatus(boardAlias: string, data: { status_id: string, guid: string, role: string }): Observable<{
        message: string
    }> {
        return this.post('instance/crm/boards/node-status/members/' + boardAlias, data);
    }

    /**
     * Удаление пользователя из статуса
     * @param boardAlias
     * @param data
     */
    deleteMemberFromStatus(boardAlias: string, data: { status_id: string, guid: string, role: string }): Observable<{
        message: string
    }> {
        return this.delete('instance/crm/boards/node-status/members/' + boardAlias, data);
    }

    /**
     * Получение всех поручений пользователя
     * @param guid
     * @param pagination
     */
    getAssignments(guid: string, pagination: any): Observable<{
        current_page: number,
        data: AssignmentModel[],
        page_count: number
    }> {
        return this.get('instance/crm/assignments/user/' + guid, pagination);
    }

    /**
     * Создать поручение
     * @param data
     */
    createAssignment(
        data: {
            title: string,
            content: string,
        }
    ): Observable<any> {
        return this.post('instance/crm/assignments/create', data);
    }

    /**
     * Получение поручения по ID
     * @param id
     */
    getAssignmentById(id: number): Observable<{ assignment: AssignmentModel }> {
        return this.get('instance/crm/assignments/' + id);
    }

    /**
     * Добавление исполнителя в поручение.
     * Добавить можно департамент, отдел или пользователя
     * @param assignmentId
     * @param data
     */
    addExecutorForAssignment(assignmentId: number, data: {
        executor_type: 'user' | 'department' | 'division',
        executor_id: number | string
    }): Observable<{}> {
        return this.post('instance/crm/assignments/set_executor/' + assignmentId, data);
    }

    /**
     * Удаление исполнителя из поручения.
     * @param assignmentId
     * @param data
     */
    delExecutorForAssignment(assignmentId: number, data: {
        executor_type: 'user' | 'department' | 'division',
        executor_id: number | string
    }): Observable<IAssignmentUpdateResult> {
        return this.delete('instance/crm/assignments/del_executor/' + assignmentId, data);
    }

    /**
     * Обновление поручения
     * @param assignmentId
     * @param data
     */
    editAssignment(assignmentId: number, data: {
                       title?: string,
                       content?: string,
                       due_date?: string,
                       due_time?: string
                   }
    ): Observable<any> {
        return this.put('instance/crm/assignments/update/' + assignmentId, data);
    }

    /**
     * Изменение статуса поручения
     * @param status 'new' | 'in_progress' | 'executed' | 'archived'
     * @param assignmentId
     */
    changeAssignmentStatus(status: string, assignmentId: number): Observable<any> {
        return this.put('instance/crm/assignments/status/' + assignmentId, {status});
    }

    /**
     * Удалить поручение
     * @param id
     */
    deleteAssignmentById(id: number): Observable<any> {
        return this.delete('instance/crm/assignments/delete/' + id);
    }

    /**
     * Получение комментариев поручения
     * @param id
     */
    getAssignmentTasks(id: number): Observable<any> {
        return this.get('instance/crm/assignments/' + id + '/comments');
    }

    /**
     * Редактировать комментарий поручения
     * @param commentId
     * @param commentMessage
     */
    editAssignmentComment(commentId: number, commentMessage: string): Observable<any> {
        return this.put('instance/crm/assignments/comment/' + commentId + '/update', {comment: commentMessage});
    }

    /**
     * Создать комментарий в поручении
     * @param assignmentId
     * @param message
     */
    sendAssignmentComment(assignmentId: number, message: string): Observable<any> {
        return this.post('instance/crm/assignments/' + assignmentId + '/comment', {comment: message});
    }

    /**
     * Удалить комментарий в поручении
     * @param commentId
     */
    deleteAssignmentComment(commentId: number): Observable<any> {
        return this.delete('instance/crm/assignments/comment/' + commentId);
    }
}
